import axios, { AxiosError, AxiosHeaders, AxiosInstance } from 'axios'
import { isPast, isValid } from 'date-fns'
import store from 'store'
import { v4 as uuidv4 } from 'uuid'
import { globalErrorStore } from '@opallabs/hooks'
import DummyClient from './DummyClient'

export { parseJSONApiResponse } from './parseJSONApiResponse'
export { serializeJSONApiData } from './serializeJSONApiData'

const getFromStorage = (key: string) => {
  try {
    return store.get(key)
  } catch (e) {
    if (e instanceof Error) {
      console.error('[Api.httpClient.getFromStorage]', key, e.message)
    } else {
      console.error('[Api.httpClient.getFromStorage]', key, 'unknown error')
    }
    return null
  }
}

const setToStorage = (key: string, value: unknown) => {
  try {
    return store.set(key, value)
  } catch (e) {
    if (e instanceof Error) {
      console.error('[Api.httpClient.setToStorage]', key, e.message)
    } else {
      console.error('[Api.httpClient.setToStorage]', key, 'unknown error')
    }
  }
}

const getCreatorGuid = () =>
  (window.creator_guid = window.creator_guid || uuidv4())

let HttpClientInstance: AxiosInstance

const buildInclude = (includes: string[]) => {
  if (!Array.isArray(includes) || includes.length === 0) return undefined

  return {
    include: includes.map((include) => include.replace(/,/g, '')).join(','),
  }
}

interface PageParams {
  limit?: number
  offset?: number
}

interface BuiltPageParams {
  'page[limit]'?: number
  'page[offset]'?: number
}

const buildPage = (page: PageParams | number = {}) => {
  const pageIsANumber = typeof page === 'number'
  if (pageIsANumber) return { page }

  const pageParams: BuiltPageParams = {}
  if (page.limit) pageParams['page[limit]'] = page.limit
  if (page.offset) pageParams['page[offset]'] = page.offset
  return pageParams
}

interface AllParams {
  include?: string[]
  page?: PageParams | number
  [key: string]: unknown
}
export const buildParams = (params: AllParams = {}) => {
  const isNotObject = typeof params !== 'object'
  const isEmptyObject =
    Object.entries(params).length === 0 && params.constructor === Object

  if (isNotObject || isEmptyObject) return undefined

  const { include, page, ...additionalParams } = params

  return {
    params: {
      ...(include && buildInclude(include)),
      ...buildPage(page),
      ...additionalParams,
    },
  }
}

const accessTokenExpired = () => {
  const expiryDate = new Date(getFromStorage('accessTokenExpires'))

  return !isValid(expiryDate) || isPast(expiryDate)
}

const UPGRADE_COOLDOWN_MS = 60_000

let upgradeBlockedUntil = 0

const upgradeToApiToken = async (): Promise<string | null> => {
  try {
    const response = await HttpClient().get('/upgrade_to_api_token')

    if (response.status === 200) {
      setToStorage('accessTokenExpires', response.data.expires_at)
      setToStorage('accessToken', response.data.token)
      return response.data.token
    }
    // Intentionally not throwing errors here, the parent API
    // call itself will fail without the session and surface errors appropriately.
    // This is causing issues during tests where the /upgrade endpoint
    // is failing asyncronously within tests due to changes in how Node 16 handles
    // errors and logging out of promises that resolve outside of the test run.
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 401) {
      upgradeBlockedUntil = Date.now() + UPGRADE_COOLDOWN_MS
      globalErrorStore.getState().setErrorCode('user_session_expired')
    }
  }

  return null
}

let pendingUpgrade: Promise<string | null> | null = null

/**
 * The access token to present right now, minting a replacement when the stored one has
 * expired. Returns null when none could be obtained.
 *
 * Concurrent callers share a single upgrade request: the server deactivates the previous
 * token when it issues a new one, so overlapping upgrades would leave the earlier caller
 * holding a revoked token.
 *
 * After an upgrade gets a 401, requests do not start another upgrade for a minute,
 * because without a session each one also gets a 401. After that minute, one upgrade
 * tries again, so the tab recovers if the session comes back. A valid token in
 * storage, for example from a log in on another tab, ends the wait.
 */
export const ensureFreshAccessToken = async (): Promise<string | null> => {
  const currentToken = getFromStorage('accessToken')
  if (currentToken !== undefined && !accessTokenExpired()) {
    upgradeBlockedUntil = 0
    return currentToken
  }
  if (Date.now() < upgradeBlockedUntil) return null

  pendingUpgrade ??= upgradeToApiToken().finally(() => {
    pendingUpgrade = null
  })

  return pendingUpgrade
}

export const refreshClient = () => {
  const headers = new AxiosHeaders({
    Accept: 'application/json',
    'Creator-Guid': getCreatorGuid(),
  })
  const authToken = getFromStorage('accessToken')
  if (authToken) headers.set('Session-Token', authToken)

  HttpClientInstance = axios.create({
    headers,
  })

  HttpClientInstance.interceptors.request.use(async (config) => {
    if (config.url === '/upgrade_to_api_token') {
      const configCopy = { ...config }
      delete configCopy.headers['Session-Token']
      return configCopy
    }

    const token = await ensureFreshAccessToken()
    if (token === null) return config

    return {
      ...config,
      headers: config.headers.set('Session-Token', token),
    }
  })

  return HttpClientInstance
}

export const HttpClient = () => {
  if (HttpClientInstance) {
    return HttpClientInstance
  }
  return refreshClient()
}

const warn = () =>
  console.warn(
    'HttpClient is a function that gets a usable client, not the client itself. Please call it to get the client. Ex: `HttpClient().get(...)`.'
  )

HttpClient.get = warn
HttpClient.post = warn
HttpClient.put = warn
HttpClient.patch = warn
HttpClient.del = warn
HttpClient.delete = warn

HttpClient.dummy = <T>(retVal: T) => new DummyClient(retVal)

export { DummyClient }

export default HttpClient
